import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicLayout } from "@/components/site/PublicLayout";
import { IssueCard } from "@/components/site/IssueCard";
import { HumanCountBanner } from "@/components/site/HumanCountBanner";
import { StoreButtons } from "@/components/site/StoreButtons";
import { SupportCohnect } from "@/components/site/SupportCohnect";
import { HeroDecor, PostageStamp } from "@/components/site/HeroDecor";
import { Users, Link2, Lock, BookOpen } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
};
const clip = (t, n = 180) => (t && t.length > n ? `${t.slice(0, n).trimEnd()}…` : t);

export default function HomePage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/issues?limit=4").then((r) => setIssues(r.data || [])).finally(() => setLoading(false));
  }, []);

  const featured = issues[0];
  const others = issues.slice(1, 4);

  const goToLetter = () => {
    navigate(featured?.slug ? `/issues/${featured.slug}` : "/issues");
  };

  const steps = [
    { n: "01", t: "Get a prompt", b: "Each week, one simple question brings someone to mind." },
    { n: "02", t: "Send a simple message", b: "Use a copy-and-paste script or write your own." },
    { n: "03", t: "See how others are cohnecting", b: "Join the Discord community and share the moment." },
  ];

  const appFeatures = [
    { icon: Users, t: "People, places & things", b: "Add the humans, spots, and objects that matter and see them all in one living map." },
    { icon: Link2, t: "Plain-language links", b: "Connect anything in your own words — “introduced me to,” “works at,” “lives in.”" },
    { icon: Lock, t: "Private to you", b: "No account, no ads, no feeds. Your map stays on your device and belongs to you." },
  ];

  return (
    <PublicLayout>
      {/* TOP — hero: app promotion (left) + latest weekly letter (right) */}
      <section className="relative overflow-hidden hero-wash border-b-2 border-[color:var(--coh-ink)]">
        <HeroDecor />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid gap-8 lg:gap-12 lg:grid-cols-2 items-start">
            {/* APP CARD (primary) */}
            <div data-testid="home-app-box" className="hero-card nb-box flex flex-col p-5 sm:p-7">
              <div className="flex items-center justify-between gap-3">
                <div className="kicker">The Cohnect App</div>
                <span data-testid="app-coming-soon" className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--coh-on-accent)]">
                  Coming Soon
                </span>
              </div>
              <div className="mt-4 rounded-md border-2 border-[color:var(--coh-ink)] overflow-hidden bg-[color:var(--coh-paper)]">
                <img src="/brand/feature-map.png" alt="A Cohnect map showing how people, places and events connect" className="block w-full h-[220px] sm:h-[260px] object-cover object-top" />
              </div>
              <h1 className="mt-6 font-display text-[28px] sm:text-4xl font-extrabold tracking-tight leading-[1.05] text-[color:var(--coh-ink)]">
                Remember how your world{" "}
                <span className="relative whitespace-nowrap">connects.
                  <span aria-hidden="true" className="absolute left-0 -bottom-1 h-[5px] w-full rounded-full bg-[color:var(--coh-blue)]" />
                </span>
              </h1>
              <div className="mt-4 border-l-4 border-[color:var(--coh-blue)] pl-4">
                <p className="text-sm sm:text-base text-[color:var(--coh-ink-muted)] leading-relaxed">
                  A private, visual map for keeping track of the people, places, and things that
                  matter to you.
                </p>
              </div>
              <div className="mt-auto pt-7">
                <StoreButtons />
                <p className="mt-4 text-xs text-[color:var(--coh-ink-muted)] font-mono uppercase tracking-[0.14em]">Free · No account · Private to you</p>
              </div>
            </div>

            {/* LATEST LETTER CARD (supporting) */}
            <div
              onClick={goToLetter}
              data-testid="home-newsletter-box"
              className="hero-card nb-box nb-interactive relative flex flex-col p-5 sm:p-7 lg:mt-12 cursor-pointer"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="kicker inline-flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> The Weekly Letter</div>
                <span className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper)] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--coh-ink)]">
                  Free
                </span>
              </div>

              {loading ? (
                <div className="mt-4 flex-1">
                  <div className="h-4 w-32 bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" />
                  <div className="mt-4 h-8 w-3/4 bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" />
                  <div className="mt-4 h-20 w-full bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" />
                </div>
              ) : featured ? (
                <div className="mt-4 flex-1">
                  <div className="flex items-center gap-2 text-xs text-[color:var(--coh-ink-muted)] font-mono">
                    <span className="text-[color:var(--coh-blue)]">#{String(featured.issue_number).padStart(3, "0")}</span>
                    <span>·</span>
                    <span>{formatDate(featured.published_at || featured.updated_at)}</span>
                    <span className="ml-1 inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--coh-on-accent)]">This week</span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight text-[color:var(--coh-ink)]">
                    {featured.title}
                  </h2>
                  {featured.excerpt && (
                    <p className="mt-3 text-sm sm:text-base text-[color:var(--coh-ink-muted)] leading-relaxed">{clip(featured.excerpt, 150)}</p>
                  )}
                  {featured.inspiring_quote?.text ? (
                    <figure className="pull-quote mt-5" data-testid="newsletter-pullquote">
                      <blockquote className="font-display text-lg sm:text-xl font-bold leading-snug text-[color:var(--coh-ink)]">
                        “{featured.inspiring_quote.text}”
                      </blockquote>
                      {featured.inspiring_quote.author && (
                        <figcaption className="mt-2 text-xs font-mono uppercase tracking-[0.14em] text-[color:var(--coh-ink-muted)]">— {featured.inspiring_quote.author}</figcaption>
                      )}
                    </figure>
                  ) : (
                    <div className="mt-5 border-l-4 border-[color:var(--coh-blue)] pl-4" data-testid="newsletter-highlight">
                      <p className="font-display text-base sm:text-lg font-bold leading-snug text-[color:var(--coh-ink)]">{featured.theme ? `This week: ${featured.theme}` : "One reconnection prompt and one small action, inside."}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 flex-1 flex items-center">
                  <p className="text-base text-[color:var(--coh-ink-muted)] leading-relaxed">
                    A new issue is on its way. Check back soon — or browse the archive below.
                  </p>
                </div>
              )}

              <PostageStamp className="pointer-events-none absolute bottom-16 right-4 hidden rotate-6 sm:block" />
              <div className="mt-auto pt-6">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goToLetter(); }}
                  data-testid="home-read-letter"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[color:var(--coh-blue)] underline underline-offset-4 decoration-2 hover:decoration-[3px]"
                >
                  Read this week’s letter <span aria-hidden="true" className="font-mono">→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APP SECTION — real app, phone screenshot */}
      <section id="app" className="bg-[color:var(--coh-paper-2)] border-b-2 border-[color:var(--coh-ink)] scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid gap-10 lg:gap-14 lg:grid-cols-[1.05fr,0.95fr] items-center">
            <div>
              <div className="kicker inline-flex items-center gap-2">The App <span className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] px-2 py-0.5 text-[10px] text-[color:var(--coh-on-accent)]">Coming Soon</span></div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-[-0.03em] leading-[1.05] text-[color:var(--coh-ink)]">
                A living constellation of your connections.
              </h2>
              <p className="mt-5 text-lg text-[color:var(--coh-ink-muted)] max-w-xl leading-relaxed">
                Cohnect brings the people, places, and things in your life together as stars on a
                map. Add connections in your own words, see how everything relates, and let your
                constellation grow with you. Private to you, always.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {appFeatures.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.t}
                      data-testid={`app-feature-${i}`}
                      className="nb-box nb-interactive flex flex-col items-center justify-start text-center p-6"
                    >
                      <div className="inline-flex h-14 w-14 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] text-[color:var(--coh-on-accent)] shadow-nb-sm">
                        <Icon className="h-7 w-7" strokeWidth={2.25} />
                      </div>
                      <h3 className="mt-4 font-display text-base sm:text-lg font-extrabold leading-tight tracking-tight text-[color:var(--coh-ink)]">{f.t}</h3>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8">
                <StoreButtons />
                <p className="mt-3 text-xs text-[color:var(--coh-ink-muted)] font-mono uppercase tracking-[0.14em]">Free · No account · No ads · Private to you</p>
              </div>
            </div>
            <div className="justify-self-center">
              <div className="inline-block border-2 border-[color:var(--coh-ink)] shadow-nb bg-[color:var(--coh-ink)] p-2">
                <img src="/brand/phone-focus.png" alt="The Cohnect app showing a personal connection map" className="block w-full max-w-[280px] h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Cohnect — directly under the app band */}
      <SupportCohnect />

      {/* How the letter works */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 pb-4">
        <div className="kicker">How the letter works</div>
        <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[color:var(--coh-ink)]">Three small ideas, once a week.</h3>
        <p className="mt-5 max-w-3xl text-base sm:text-lg text-[color:var(--coh-ink-muted)] leading-relaxed">
          A free weekly letter about how people connect, from relationships and psychology to
          technology and life around the world. Each issue includes one simple prompt to reconnect
          and one small action you can take in just a few minutes.
        </p>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="nb-box p-6" data-testid={`home-howitworks-step-${s.n}`}>
              <div className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] px-2 py-1 font-mono text-xs font-semibold tracking-[0.14em] text-[color:var(--coh-ink)]">{s.n}</div>
              <h4 className="mt-4 font-display text-xl font-extrabold text-[color:var(--coh-ink)]">{s.t}</h4>
              <p className="mt-2 text-sm text-[color:var(--coh-ink-muted)] leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      <HumanCountBanner />

      {/* Recent issues */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="kicker">Latest</div>
            <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[color:var(--coh-ink)]">Recent issues</h3>
          </div>
          <Link to="/issues" data-testid="home-view-all-issues" className="text-sm font-semibold text-[color:var(--coh-blue)] underline underline-offset-4 decoration-2 hover:decoration-[3px]">
            View all <span aria-hidden="true" className="font-mono">↗</span>
          </Link>
        </div>
        {loading ? (
          <div className="h-40 nb-box animate-pulse" />
        ) : issues.length === 0 ? (
          <div className="nb-box p-8 text-center text-[color:var(--coh-ink-muted)]">No published issues yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured && <IssueCard issue={featured} featured />}
            {others.map((i) => <IssueCard key={i.id} issue={i} />)}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
